import { ExclamationCircleOutlined, LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Tabs } from 'antd';
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';
import apiClient from '../utils/axiosConfig';

interface UserAuthProps {
  visible: boolean;
  onClose: () => void;
}

const UserAuth: React.FC<UserAuthProps> = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registerEmail, setRegisterEmail] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();

  const { t } = useI18n();
  const { login } = useAuth();

  // 处理模态框关闭，重置所有表单和状态
  const handleClose = () => {
    loginForm.resetFields();
    registerForm.resetFields();
    setVerificationSent(false);
    setRegisterEmail(null);
    setActiveTab('login');
    onClose();
  };

  // 实时校验邀请码并展示邀请人
  const handleInvitationChange = async () => {
    const raw = registerForm.getFieldValue('invitationCode');
    const code = (raw || '').trim().toUpperCase();

    if (code.length !== 4) {
      registerForm.setFields([{ name: 'inviterName', value: undefined }]);
      return;
    }
    try {
      const resp = await apiClient.get('/auth/inviter', { params: { code } });
      if (resp.data?.exists) {
        const name = resp.data.user?.name || resp.data.user?.email || t('messages.invitation.inviter');
        registerForm.setFields([{ name: 'inviterName', value: name }]);
      } else {
        registerForm.setFields([{ name: 'inviterName', value: undefined }]);
        message.error(t('messages.invitation.invitationCodeInvalid'));
      }
    } catch (_e) {
      registerForm.setFields([{ name: 'inviterName', value: undefined }]);
      message.error(t('messages.invitation.invitationCodeInvalid'));
    }
  };

  const handleLogin = async (values: any) => {
    setLoading(true);
    try {
      console.log('Login values:', values);

      const response = await apiClient.post('/auth/login', {
        email: values.email,
        password: values.password
      });

      if (response.data.userExists && response.data.unactivated) {
        // 用户已存在但未激活，显示确认对话框
        Modal.confirm({
          title: t('messages.auth.accountNotActivated'),
          icon: <ExclamationCircleOutlined />,
          content: response.data.message,
          okText: t('messages.auth.resendVerificationCode'),
          cancelText: t('common.cancel'),
          onOk: async () => {
            try {
              setLoading(true);
              const resendResponse = await apiClient.post('/auth/resend-verification', {
                contact: response.data.email || response.data.phoneNumber,
                type: response.data.email ? 'EMAIL' : 'PHONE'
              });

              if (resendResponse.data.success) {
                message.success(t('messages.auth.verificationCodeResent'));
              } else {
                message.error(resendResponse.data.error || t('messages.auth.sendFailed'));
              }
            } catch (error: any) {
              message.error(error.response?.data?.error || t('messages.auth.sendFailed'));
            } finally {
              setLoading(false);
            }
          }
        });
      } else if (response.data.token) {
        const { token, user } = response.data;
        // 使用useAuth的login方法更新认证状态
        login(token, user);
        message.success(t('messages.auth.loginSuccess'));
        handleClose();
      } else {
        message.error(response.data.error || t('messages.auth.loginFailed'));
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || t('messages.auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: any) => {
    setLoading(true);
    try {

      const response = await apiClient.post('/auth/register', {
        name: values.name,
        email: values.email,
        password: values.password,
        confirmPassword: values.confirmPassword,
        phoneNumber: '', // 这个组件暂时只支持邮箱注册
        invitationCode: values.invitationCode || null,
        userType: 'tw' // webpage用户类型为tw
      });

      if (response.data.userExists && response.data.unactivated) {
        // 用户已存在但未激活，显示确认对话框
        Modal.confirm({
          title: t('messages.auth.accountNotActivated'),
          icon: <ExclamationCircleOutlined />,
          content: response.data.message,
          okText: t('messages.auth.resendVerificationCode'),
          cancelText: t('common.cancel'),
          onOk: async () => {
            try {
              setLoading(true);
              const resendResponse = await apiClient.post('/auth/resend-verification', {
                contact: response.data.email || response.data.phoneNumber,
                type: response.data.email ? 'EMAIL' : 'PHONE'
              });

              if (resendResponse.data.success) {
                message.success(t('messages.auth.verificationCodeResent'));
              } else {
                message.error(resendResponse.data.error || t('messages.auth.sendFailed'));
              }
            } catch (error: any) {
              message.error(error.response?.data?.error || t('messages.auth.sendFailed'));
            } finally {
              setLoading(false);
            }
          }
        });
      } else if (response.data.requiresVerification) {
        // 新用户注册成功，验证码已发送，开启验证码输入并激活“完成注册”按钮
        setVerificationSent(true);
        setRegisterEmail(values.email);
        message.success(response.data.message || t('messages.auth.verificationRequired'));
      } else if (response.data.message) {
        message.success(t('messages.auth.registerSuccess'));
        handleClose();
      } else {
        const errMsg = response.data.error || t('messages.auth.registerFailed');
        // 若提示“邮箱已被注册”，询问是否重发验证码（兼容后端未返回 unactivated 的情况）
        if (errMsg.includes(t('messages.auth.emailAlreadyRegistered'))) {
          Modal.confirm({
            title: t('messages.auth.emailAlreadyRegistered'),
            icon: <ExclamationCircleOutlined />,
            content: t('messages.auth.emailAlreadyRegistered'),
            okText: t('messages.auth.resendCode'),
            cancelText: t('common.cancel'),
            onOk: async () => {
              try {
                setLoading(true);
                const resend = await apiClient.post('/auth/resend-verification', {
                  contact: values.email,
                  type: 'EMAIL'
                });
                if (resend.data && resend.data.success) {
                  message.success(t('messages.auth.verificationCodeResent'));
                  setVerificationSent(true);
                  setRegisterEmail(values.email);
                } else {
                  message.error(resend.data?.error || t('messages.auth.sendFailed'));
                }
              } catch (e: any) {
                message.error(e.response?.data?.error || t('messages.auth.sendFailed'));
              } finally {
                setLoading(false);
              }
            }
          });
        } else {
          message.error(errMsg);
        }
      }
      // 兜底：部分环境未返回 requiresVerification 字段，但已发送邮件
      if (!verificationSent) {
        setVerificationSent(true);
        setRegisterEmail(values.email);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || t('messages.auth.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegister = async () => {
    try {
      const code = registerForm.getFieldValue('code');
      const email = registerEmail || registerForm.getFieldValue('email');
      if (!email || !code) {
        message.error(t('messages.auth.pleaseEnterEmailAndCode'));
        return;
      }
      setLoading(true);
      const resp = await apiClient.post('/auth/verify', {
        contact: email,
        code,
        type: 'EMAIL'
      });
      if (resp.data && resp.data.success) {
        message.success(t('messages.auth.verificationSuccess'));
        // 切回登录页签
        registerForm.resetFields(['code']);
        setVerificationSent(false);
        // 预填登录邮箱并切换到登录页签
        const email = registerEmail || registerForm.getFieldValue('email');
        loginForm.setFieldsValue({ email, password: '' });
        setActiveTab('login');
      } else {
        message.error(resp.data?.error || t('messages.auth.verificationFailed'));
      }
    } catch (e: any) {
      message.error(e.response?.data?.error || t('messages.auth.verificationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 发送/重发验证码：首次走注册创建用户，其后走重发
  const handleSendCode = async () => {
    try {
      const values = await registerForm.validateFields(['name', 'email', 'password', 'confirmPassword', 'invitationCode']);
      console.log('handleSendCode values:', values);
      setLoading(true);
      if (!verificationSent) {
        // 首次：走注册接口，后端会创建未激活用户并发送验证码
        const response = await apiClient.post('/auth/register', {
          name: values.name,
          email: values.email,
          password: values.password,
          confirmPassword: values.confirmPassword,
          phoneNumber: '',
          invitationCode: values.invitationCode || null
        });
        if (response.data?.requiresVerification) {
          setVerificationSent(true);
          setRegisterEmail(values.email);
          message.success(response.data.message || t('messages.auth.verificationRequired'));
        } else if (response.data?.userExists && response.data?.unactivated) {
          setVerificationSent(true);
          setRegisterEmail(values.email);
          message.info(t('messages.auth.accountNotActivatedInfo'));
        } else if (response.data?.message) {
          message.success(response.data.message);
        } else if (response.data?.error) {
          message.error(response.data.error);
        }
      } else {
        // 已发送：调用重发
        const email = registerEmail || values.email;
        const resend = await apiClient.post('/auth/resend-verification', {
          contact: email,
          type: 'EMAIL'
        });
        if (resend.data?.success) {
          message.success(t('messages.auth.verificationCodeResent'));
        } else {
          message.error(resend.data?.error || t('messages.auth.sendFailed'));
        }
      }
    } catch (e: any) {
      if (e?.errorFields) {
        // 表单校验错误
        return;
      }
      const errMsg = e.response?.data?.error || t('messages.auth.sendFailed');
      if (errMsg.includes(t('messages.auth.emailAlreadyRegistered'))) {
        Modal.confirm({
          title: t('messages.auth.emailAlreadyRegistered'),
          icon: <ExclamationCircleOutlined />,
          content: t('messages.auth.emailAlreadyRegistered'),
          okText: t('messages.auth.resendCode'),
          cancelText: t('common.cancel'),
          onOk: async () => {
            try {
              const email = registerEmail || registerForm.getFieldValue('email');
              setLoading(true);
              const resend = await apiClient.post('/auth/resend-verification', {
                contact: email,
                type: 'EMAIL'
              });
              if (resend.data?.success) {
                message.success(t('messages.auth.verificationCodeResent'));
                setVerificationSent(true);
                setRegisterEmail(email);
              } else {
                message.error(resend.data?.error || t('messages.auth.sendFailed'));
              }
            } finally {
              setLoading(false);
            }
          }
        });
      } else {
        message.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'login',
      label: t('messages.auth.login'),
      children: (
        <Form form={loginForm} onFinish={handleLogin} layout="vertical">
          <Form.Item
            name="email"
            rules={[{ required: true, message: t('messages.auth.pleaseEnterEmail') }]}
          >
            <Input prefix={<MailOutlined />} placeholder={t('messages.auth.email')} />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('messages.auth.pleaseEnterPassword') }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder={t('messages.auth.password')} />
          </Form.Item>
          <Form.Item>
            <Button className="btn-primary-unified btn-block-unified" htmlType="submit" loading={loading}>
              {t('messages.auth.login')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'register',
      label: t('messages.auth.register'),
      children: (
        <Form form={registerForm} onFinish={handleRegister} layout="vertical">
          <Form.Item
            name="name"
            rules={[{ required: true, message: t('messages.auth.pleaseEnterName') }]}
          >
            <Input prefix={<UserOutlined />} placeholder={t('messages.auth.name')} />
          </Form.Item>
          <Form.Item
            name="email"
            rules={[{ required: true, message: t('messages.auth.pleaseEnterEmail') }]}
          >
            <Input prefix={<MailOutlined />} placeholder={t('messages.auth.email')} />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('messages.auth.pleaseEnterPassword') }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder={t('messages.auth.password')} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            rules={[
              { required: true, message: t('messages.auth.pleaseConfirmPassword') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('messages.auth.passwordMismatch')));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder={t('messages.auth.confirmPassword')} />
          </Form.Item>
          <Form.Item
            name="invitationCode"
            label={t('messages.invitation.invitationCode')}
            rules={[
              { len: 4, message: t('messages.invitation.invitationCodeLength') }
            ]}
          >
            <Input
              placeholder={t('messages.invitation.invitationCodePlaceholder')}
              maxLength={4}
              onChange={handleInvitationChange}
            />
          </Form.Item>
          {/* 展示邀请人 */}
          <Form.Item name="inviterName" label={t('messages.invitation.inviter')} shouldUpdate={false}>
            <Input disabled placeholder={t('messages.invitation.inviter')} />
          </Form.Item>
          {/* 验证码输入，在发送后展示 */}
          {verificationSent && (
            <Form.Item
              name="code"
              rules={[{ required: true, message: t('messages.auth.pleaseEnterVerificationCode') }, { len: 4, message: t('messages.auth.verificationCodeLength') }]}
            >
              <Input placeholder={t('messages.auth.enterVerificationCode')} maxLength={4} />
            </Form.Item>
          )}
          <Form.Item>
            <Button className="btn-secondary-unified btn-block-unified" onClick={handleSendCode} loading={loading}>
              {verificationSent ? t('messages.auth.resendVerificationCodeButton') : t('messages.auth.sendVerificationCode')}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button className="btn-primary-unified btn-block-unified" onClick={handleVerifyRegister} loading={loading} disabled={!verificationSent}>
              {t('messages.auth.completeRegistration')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      title={t('messages.auth.userLoginRegister')}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={400}
    >
      <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k as any)} items={tabItems} />
    </Modal>
  );
};

export default UserAuth;
